import React, { useState } from "react";
import axios from "axios";
import { Eye, ThumbsUp, MessageCircle, Clock, Youtube, Sparkles, Quote, Lightbulb, Wand2, Copy, Check, Loader2, Share2 } from "lucide-react";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { toast } from "sonner";
import { SwipeSaveButton } from "./SwipeFile";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatNumber = (num) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return String(num);
};

function StealHookDialog({ video, index }) {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("");
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const generate = async () => {
    if (!topic.trim()) {
      toast.error("Enter your topic or niche first");
      return;
    }
    setLoading(true);
    setHooks([]);
    try {
      const { data } = await axios.post(`${API}/hooks/steal`, {
        source_transcript: video.transcript_full_300_words || video.transcript_first_150_words,
        topic: topic.trim(),
        hook_type: video.analysis?.hook_type || "",
        copywriting_frameworks: video.analysis?.copywriting_frameworks || [],
        patterns: video.analysis?.patterns || [],
      });
      setHooks(data.hooks || []);
    } catch (e) {
      toast.error("Failed to generate hooks");
    } finally {
      setLoading(false);
    }
  };

  const copyHook = async (text, i) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(i);
      toast.success("Hook copied");
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.3)] hover:shadow-[0_0_24px_rgba(255,0,0,0.5)]"
          data-testid={`steal-hook-btn-${index}`}
        >
          <Wand2 className="w-4 h-4 mr-1.5" />
          Steal this hook
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-zinc-950 border border-white/10 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Wand2 className="w-5 h-5 text-red-500" />
            Steal this hook
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Reuses the same framework ({video.analysis?.hook_type || "—"}) on your own topic.
            3 fresh hook variations, ready to read on camera.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor={`steal-topic-${index}`} className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              Your video topic / niche
            </Label>
            <div className="flex gap-2 mt-2">
              <Input
                id={`steal-topic-${index}`}
                data-testid={`steal-hook-topic-${index}`}
                placeholder="e.g. AI coding tools for solo founders"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !loading && generate()}
                className="bg-black/40 border-white/10 text-white"
              />
              <Button
                onClick={generate}
                disabled={loading || !topic.trim()}
                className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_15px_rgba(255,0,0,0.3)]"
                data-testid={`steal-hook-generate-${index}`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating
                  </>
                ) : (
                  "Generate"
                )}
              </Button>
            </div>
          </div>

          {hooks.length > 0 && (
            <div className="space-y-3" data-testid={`steal-hook-results-${index}`}>
              {hooks.map((h, i) => (
                <div
                  key={`${h.slice(0, 40)}-${i}`}
                  className="rounded-xl p-4 bg-red-500/[0.06] border border-red-500/15"
                  data-testid={`steal-hook-result-${index}-${i}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <Badge className="bg-red-500/15 text-red-300 border border-red-500/25 hover:bg-red-500/15">
                      Variation {i + 1}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyHook(h, i)}
                      className="h-8 text-zinc-400 hover:text-white hover:bg-white/5"
                      data-testid={`steal-hook-copy-${index}-${i}`}
                    >
                      {copiedIdx === i ? (
                        <>
                          <Check className="w-4 h-4 mr-1" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" /> Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-zinc-200 leading-relaxed">{h}</p>
                  <p className="text-xs text-zinc-500 mt-2">{h.split(/\s+/).length} words</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CompetitorCard({ video, index, savedSet, onSavedChange }) {
  // Always show the full 300-word transcript when we have it — the earlier
  // "150 words with Read more toggle" UX was a hangover from when the backend
  // only fetched the first 150. Since Feb 2026 the backend fetches 300 words
  // per the user's request, so there's no expand step to gate anymore.
  const transcript = video.transcript_full_300_words || video.transcript_first_150_words || "";

  const analysis = video.analysis;

  return (
    <div
      data-testid={`competitor-card-${index}`}
      className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl p-6 hover:bg-white/[0.04] hover:border-white/[0.15] transition-all duration-300"
    >
      <div className="flex items-start gap-5">
        {/* Rank badge */}
        <div className="flex-shrink-0">
          <div className="w-9 h-9 rounded-xl bg-red-500/10 border border-red-500/25 text-red-400 flex items-center justify-center font-semibold text-sm shadow-[0_0_18px_-6px_rgba(255,0,0,0.5)]">
            #{index + 1}
          </div>
        </div>

        {/* Thumbnail */}
        <div className="flex-shrink-0">
          <img
            src={video.thumbnail_url}
            alt={video.title}
            className="rounded-lg object-cover border border-white/10"
            style={{ width: 144, height: 81 }}
          />
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-base mb-2 line-clamp-2" data-testid={`competitor-title-${index}`}>
            {video.title}
          </h3>

          <div className="mb-3">
            <a
              href={video.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 font-medium text-sm transition-colors"
              data-testid={`competitor-watch-link-${index}`}
            >
              <Youtube className="w-4 h-4" />
              <span>Watch on YouTube</span>
            </a>
          </div>

          {/* Metrics */}
          <div className="flex items-center flex-wrap gap-x-5 gap-y-1 mb-4 text-sm text-zinc-400">
            <div className="flex items-center gap-1.5"><Eye className="w-4 h-4 text-zinc-500" /><span><span className="text-zinc-200">{formatNumber(video.view_count)}</span> views</span></div>
            <div className="flex items-center gap-1.5"><ThumbsUp className="w-4 h-4 text-zinc-500" /><span><span className="text-zinc-200">{formatNumber(video.like_count)}</span> likes</span></div>
            <div className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-zinc-500" /><span><span className="text-zinc-200">{formatNumber(video.comment_count)}</span> comments</span></div>
            <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-zinc-500" /><span>{video.duration}</span></div>
          </div>

          {/* AI Hook Analysis */}
          {analysis && (
            <div
              className="rounded-xl bg-red-500/[0.05] border border-red-500/15 p-4 mb-3"
              data-testid={`competitor-analysis-${index}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white text-sm">AI Hook Analysis</span>
                {analysis.hook_type && (
                  <Badge
                    className="bg-red-500/15 text-red-300 border border-red-500/25 hover:bg-red-500/15 ml-1"
                    data-testid={`competitor-hook-type-${index}`}
                  >
                    {analysis.hook_type}
                  </Badge>
                )}
              </div>

              {analysis.one_sentence_summary && (
                <p className="text-sm text-zinc-300 mb-3 leading-relaxed">
                  {analysis.one_sentence_summary}
                </p>
              )}

              {analysis.copywriting_frameworks?.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 font-semibold">
                    Frameworks
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.copywriting_frameworks.map((f, i) => (
                      <Badge key={`${f}-${i}`} className="bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/5 font-normal">
                        {f}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {analysis.patterns?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 font-semibold flex items-center gap-1">
                    <Lightbulb className="w-3 h-3" /> Tactics spotted
                  </div>
                  <ul className="space-y-1">
                    {analysis.patterns.map((p, i) => (
                      <li key={`${p}-${i}`} className="text-sm text-zinc-300 flex items-start gap-2">
                        <span className="text-red-400 mt-0.5">•</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-red-500/15 flex flex-wrap items-center gap-2">
                <StealHookDialog video={video} index={index} />
                <SwipeSaveButton
                  video={video}
                  savedSet={savedSet}
                  onSavedChange={onSavedChange}
                  index={index}
                />
                <ShareTeardownButton video={video} index={index} />
              </div>
            </div>
          )}

          {/* Opening hook transcript — always the full 300 words. */}
          <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Quote className="w-4 h-4 text-zinc-500" />
              <span className="font-medium text-zinc-300 text-sm">
                Opening Hook · 300 words
              </span>
            </div>
            <p
              className="text-zinc-300 text-sm leading-relaxed italic"
              data-testid={`competitor-transcript-${index}`}
            >
              &ldquo;{transcript}&rdquo;
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShareTeardownButton({ video, index }) {
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const createOrCopy = async () => {
    // Already have a URL? just re-copy
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        toast.success("Share link copied");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Copy failed");
      }
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/competitors/share`, {
        video_id: video.video_id,
        channel_name: video.channel_name || "",
        title: video.title,
        thumbnail_url: video.thumbnail_url || "",
        video_url: video.video_url,
        view_count: video.view_count || 0,
        like_count: video.like_count || 0,
        comment_count: video.comment_count || 0,
        duration: video.duration || "",
        transcript_first_150_words: video.transcript_first_150_words || "",
        transcript_full_300_words: video.transcript_full_300_words || "",
        analysis: video.analysis || null,
      });
      setShareUrl(data.share_url);
      try {
        await navigator.clipboard.writeText(data.share_url);
        setCopied(true);
        toast.success("Public share link copied");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.info("Share link ready (clipboard blocked) — link visible below");
      }
    } catch (e) {
      toast.error("Couldn't create share link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 w-full">
      <Button
        size="sm"
        onClick={createOrCopy}
        disabled={loading}
        variant="outline"
        className="bg-white/[0.03] border-white/15 text-zinc-200 hover:bg-white/[0.08] hover:text-white"
        data-testid={`share-teardown-btn-${index}`}
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating link</>
        ) : copied ? (
          <><Check className="w-4 h-4 mr-1.5" /> Copied</>
        ) : shareUrl ? (
          <><Copy className="w-4 h-4 mr-1.5" /> Copy share link</>
        ) : (
          <><Share2 className="w-4 h-4 mr-1.5" /> Share teardown</>
        )}
      </Button>
      {shareUrl && (
        <a
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-zinc-500 hover:text-red-400 underline underline-offset-2 truncate max-w-[260px]"
          data-testid={`share-teardown-url-${index}`}
          title={shareUrl}
        >
          {shareUrl.replace(/^https?:\/\//, "")}
        </a>
      )}
    </div>
  );
}
